import type { CmsMedia, StrapiResponse } from "./strapi";
import type { MerchantResponse } from "@/types/merchants";
import type { SiteOfTheMonthResponse } from "@/types/siteOfTheMonth";
import type { ManagedCookies } from "./nextjs";
import type {
    USPAttributesGroup,
    SeoMeta,
    SeoLink,
    SeoTitleDescription,
} from "./sites";
import type {
    LandingPageTemplateResponse,
    LowerContentBlockComponent,
} from "./landing";
import { CategoryResponse } from "./categories";
import { AuthorBlock } from "@/types/author";
import { GeoProfileResponse } from "@/types/geoProfile";
import { TitleUnitResponse } from "@/types/titleUnit";
import { AdUnitResponse } from "@/types/adUnit";
import { SESSION_COOKIE_NAME, USER_COOKIE_NAME } from "@/constants";
import type { OfferModalComponent, OfferModalObj } from "@/types/modals";
export type OffersResponse = StrapiResponse<Offer[]>;
export type OfferResponse = StrapiResponse<Offer>;
export type Offer = {
    readonly id: number;
    readonly attributes: OfferAttributes;
};

export type BonusLabelsResponse = StrapiResponse<BonusLabel[]>; // returned by calling the findMany api
export type BonusLabelResponse = StrapiResponse<BonusLabel>; // returned in a one-to-one relationship

export type BonusLabel = {
    readonly id: number;
    readonly attributes: BonusLabelAttributes;
};

type BonusLabelAttributes = {
    readonly label?: string;
};

type OfferTagline = {
    readonly data: OfferTaglineData;
};

type OfferTaglineData = {
    readonly attributes: OfferTaglineAttributes;
};

type OfferTaglineAttributes = {
    readonly tagline?: string;
};

type OfferText = {
    readonly id: number;
    readonly additional?: string;
    readonly freeSpins?: FreeSpinsAttributes;
    readonly bonus?: BonusAttributes;
};

type FreeSpinsAttributes = {
    readonly id: number;
    readonly prefix?: string;
    readonly suffix?: string;
    readonly amount?: string;
};

type BonusAttributes = {
    readonly id: number;
    readonly prefix?: string;
    readonly suffix?: string;
    readonly amount?: string;
};

type OfferInfo = {
    readonly id: number;
    readonly wagering?: WageringAttributes;
    readonly minDeposit?: MinDepositAttributes;
};

type WageringAttributes = {
    readonly id: number;
    readonly amount?: string;
    readonly suffix?: string;
    readonly activated?: boolean;
};

type MinDepositAttributes = {
    readonly id: number;
    readonly prefix?: string;
    readonly amount?: string;
    readonly suffix?: string;
    readonly activated?: boolean;
};

type OfferAttributes = {
    readonly offer_tagline: OfferTagline;
    bonus_label: BonusLabelResponse;
    readonly title: string;
    readonly subTitle: string;
    rating: string;
    readonly termsAndConditionsText: string;
    readonly termsAndConditionsMarkdown: string;
    readonly description: string;
    readonly publishedAt: Date;
    readonly createdAt: Date;
    readonly updatedAt: Date;
    readonly offerLink: string;
    readonly merchant: MerchantResponse;
    readonly deposit: string;
    readonly wagering: string;
    readonly offerText?: OfferText;
    readonly offerInfo?: OfferInfo;
    readonly votes?: number;
    readonly deadline?: Date;
    readonly ticket_price?: string;
    readonly offerImage?: CmsMedia;
};

export type OfferPageTemplateResponse = StrapiResponse<OfferPageTemplate> & {
    sanitisedOffer?: SanitisedOffer[];
    sanitisedOfferMobile?: SanitisedOffer[];
    sanitisedSiteOfTheMonth?: SanitisedOffer;
    sanitisedSiteOfTheMonthMobile?: SanitisedOffer;
    sanitisedStyles?: string;
    sanitisedStylesMobile?: string;
    offersClaimed: OffersClaimed | null;
    sanitisedOfferModal?: OfferModalObj;
    sanitisedPageFeatures?: PageFeature[];
};

export type OffersClaimed = {
    now: number;
    lifetimeOffersClaimed: number;
    nOffersClaimed: number;
    tTimePeriod: number;
};

export type OfferPageTemplate = {
    readonly id: number;
    readonly attributes: OfferPageTemplateAttributes;
};

type OfferPageTemplateAttributes = {
    readonly name: string;
    readonly offer_presentation: OfferPresentationResponse;
    readonly publishedAt: Date;
    offers: Offer[];
    readonly updatedAt: Date;
    readonly createdAt: Date;
    readonly headerBackground: CmsMedia;
    readonly title: string;
    readonly subtitle: string;
    readonly useMerchantColours: boolean;
    readonly site_of_the_month: SiteOfTheMonthResponse;
    readonly showSiteOfTheMonthSkyscraper: boolean;
    readonly showSiteOfTheMonthOfferListImage: boolean;
    readonly offerListBackgroundColour: string;
    readonly offer_layout?: OfferLayoutResponse;
    readonly orderedOffers?: OrderedOffers[] | null;
    readonly offerSlug?: string;
    readonly seoMetaField: SeoMeta[];
    readonly seoLinkField: SeoLink[];
    readonly seo_title_description?: SeoTitleDescription;
    readonly attribution_group: StrapiResponse<USPAttributesGroup>;
    readonly offer_header: StrapiResponse<OfferHeader>;

    readonly offer_list: StrapiResponse<OfferList>;

    offerLowerContentBlock?: LowerContentBlockComponent;
    sanitisedLowerContentBlockText: string;
    lowerContentTextColor: string;
    readonly categories: CategoryResponse;
    readonly lockedTermsAndConditionsText?: string;
    readonly lockedPlayButtonText?: string;
    readonly lockable?: boolean;
    readonly locked_landing_page?: LandingPageTemplateResponse;
    readonly lockedTitle?: string;
    readonly noIndex?: boolean;
    readonly hideOffersClaimedPopup?: boolean;
    readonly authorBlock: AuthorBlock;
    readonly OfferModal: OfferModalComponent;
    readonly geo_profile: GeoProfileResponse;
    readonly backgroundOverride: CmsMedia;

    readonly PF?: PageFeature[];

    /*
     *  These are far from the ideal type names as they are not descriptive at all, but they are required
     *  because of the following bug in Strapi: https://github.com/strapi/strapi/issues/13117
     *
     * Table names are automatically generated from the relations that you create in the Strapi admin panel.
     * If these table names are too long, Strapi dies.
     *
     * This problem is made worse by the use of components, which adds an unavoidable "component_" prefix to the table name.
     *
     * The unavoidable solution is to use short names for some relational fields.
     *
     * I tried a few different variants of the names, but they all failed and so this is the nuclear option.
     *
     * The names are as follows:
     * - OP: Offer Page
     * - OPD: Offer Presentation Desktop
     * - OPM: Offer Presentation Mobile
     * - OLM: Offer Layout Mobile
     * - OLD: Offer Layout Desktop
     *
     */
    readonly OP: OfferPresentationComponent;
};

export type PageFeature = (AdRow | TitleRow) & { readonly position?: number };

type AdRow = {
    readonly ad_unit?: AdUnitResponse;
    sanitisedAdUnit?: SanitisedOffer | null;
};

type TitleRow = {
    readonly title_unit?: TitleUnitResponse;
};

type OfferList = {
    readonly id: number;
    readonly attributes: OfferListAttributes;
};

type OfferListAttributes = {
    readonly name: string;
    readonly updatedAt: Date;
    readonly orderedOffers: OrderedOffers[] | null;
};

type OfferPresentationComponent = {
    readonly id: number;
    readonly OPD: OfferPresentationResponse;
    readonly OPM?: OfferPresentationResponse;
    readonly OLM?: OfferLayoutResponse;
    readonly OLD?: OfferLayoutResponse;
    readonly mobileBreakpoint?: number | null;
};

type OfferHeader = {
    readonly id: number;
    readonly attributes: OfferHeaderAttributes;
};

type OfferHeaderAttributes = {
    readonly main_text: string;
    readonly header_image: CmsMedia;
    readonly header_image_mobile: CmsMedia;
    readonly offer_start_attribution: OfferHeaderAttributions[];
    readonly offer_end_attribution: OfferHeaderAttributions[];
    readonly text_colour?: string;
    readonly dateFormat?: string;
    readonly datePrefix?: string;
    readonly subtitle?: string;
    readonly background_color?: string;
    readonly styles?: string;
    readonly title_override?: string;
    readonly attribution_location?: "standard" | "below";
};

export type OfferHeaderAttributions = {
    readonly id: number;
    readonly attribution_title: string;
    readonly attribution_sub_title: string;
    readonly attribution_image: CmsMedia;
};

export type OrderedOffers = {
    readonly id: number;
    readonly bonus_label: {
        readonly id: number;
    };
    readonly rating?: string;
};

type OfferLayoutResponse = StrapiResponse<OfferLayout>;

export type OfferLayout = {
    readonly id: number;
    readonly attributes: OfferLayoutAttributes;
};

type OfferLayoutAttributes = {
    readonly name: string;
    presentationLayoutTemplate?: string;
    sanitisedLayoutHTML?: string;
    sanitisedLayoutStyles?: string;

    readonly publishedAt: Date;
    readonly createdAt: Date;
    readonly updatedAt: Date;
};

export type OfferPresentationResponse = {
    readonly data: OfferPresentation;
};

export type OfferPresentation = {
    readonly id: number;
    readonly attributes: OfferPresentationAttributes;
};

type OfferPresentationAttributes = {
    readonly name: string;
    sanitisedHeaderStyles?: string;
    sanitisedHeaderHTML?: string;
    sanitisedStyles?: string;
    templateHTML?: string;
    headerHTML?: string;
    headerStyles?: string;
    styles?: string;
    presentationContentTemplate?: string;
    presentationContentTemplateHeader?: string;
    readonly useTemplateBuilder: boolean;
    readonly useTemplateBuilderHeader: boolean;
    readonly activatedColour?: string;
    readonly deactivatedColour?: string;
    readonly use_react_component?: string;
    readonly publishedAt: Date;
    readonly createdAt: Date;
    readonly updatedAt: Date;
};

export type SanitisedOffer = {
    htmlWithoutReplacement?: string;
    readonly unmergedOfferLink: string;
    readonly merchantSubIdSeperator: string;
    readonly id: number;
    html: string;
    offerLink: string;
    readonly merchant: MerchantResponse;
    readonly title: string;
    readonly tagline?: string;
    readonly deposit?: string;
    readonly wagering?: string;
    termsAndConditionsHTML?: string;
    readonly deadline?: Date;
    readonly offerImage?: CmsMedia;
    readonly ticket_price?: string;

    readonly freeSpinsText?: string;
    readonly bonusText?: string;
    readonly freeSpinsValue?: number;
    readonly rating?: number;
    readonly logos?: {
        primary: Logos;
        secondary: Logos;
    };

    // The following fields are currently only needed to send to GA4
    epcId?: string;
    subId?: string;
};

export type SrcSet = {
    width: number | "100vw";
    src: string;
    screenWidth?: number;
};

export type Logos = {
    id?: number;
    sizes: string;
    src: string;
    srcSet: SrcSet[];
    width?: number;
    height?: number;
};

export type GA4Offer = {
    readonly index?: number | string; // Position in the list
    readonly item_id?: number; // Offer ID
    readonly item_list_name?: string; // Offer link
    readonly item_brand?: string; // Merchant name
    readonly item_name?: string; // Free spins text
    readonly item_variant?: string; // Bonus text
    readonly item_category?: string; // Merchant type (e.g Casino, Bingo)
    readonly item_category2?: number; // Free spins number
    readonly item_category3?: string; // Min deposit number
    readonly item_category4?: string; // Wagering number
    readonly item_category5?: number; // Rating
};

export enum ClickoutLocation {
    Skyscraper = "skyscraper",
    Offer = "offer",
    CompetitionGrid = "competition-grid",
    AdRow = "ad-row",
    Modal = "modal",
}

export type SubIdProps = {
    sessionCookie: ManagedCookies[typeof SESSION_COOKIE_NAME];
    userCookie: ManagedCookies[typeof USER_COOKIE_NAME];
    shortCode: string;
};

export type DeviceType = "mobile" | "tablet" | "desktop";
